import { Database } from "./supabaseTypesImport";

export function isShared(
	entity: FileWithShare
): boolean {
	if (!entity || !entity.shared_ids) {
		return false;
	}
	if (
		(entity as FileWithShare).shared_until &&
		new Date((entity as FileWithShare).shared_until as string) < new Date()
	) {
		return false;
	}
	// Filter null elements
	return entity.shared_ids[0] !== null;
}

export function wasShared(
	entity: FileWithShare
): boolean {
	if (!entity || !entity.shared_ids) {
		return false;
	}
	return entity.shared_ids.filter((j) => j).length > 0;
}

export type SharedTableType = "jobs" | "patients" | "files";

export enum ActivityTypeEnum {
	JOB_REQUEST_STATUS = "job_request_status",
	SHARED_JOB_REQUEST = "shared_job_request",
	SHARED_JOB = "shared_job",
	SHARED_PATIENT = "shared_patient",
	SHARED_DOCUMENT = "shared_document",
	SHARED_FILE = "shared_file",
	MESSAGE_GENERAL = "message_general",
	SRG = "srg",
}

export enum SupabaseShareEntityViewEnum {
	FILES_WITH_SHARES = "files_with_shares",
	JOBS_WITH_SHARES = "jobs_with_shares",
	SHARES_WITH_FILE_INFO = "shares_with_file_info",
	JOBS_WITH_PATIENT = "jobs_with_patient",
}

export enum SupabaseViewEnum {
	COMPLETED_JOB_DOCUMENTS_VIEW = "completed_job_documents_view",
	JOB_ITEMS_STATS = "job_items_stats",
	RECYCLE_BIN_FILES = "recycle_bin_files",
	RECYCLE_BIN_JOB_DOCUMENTS = "recycle_bin_job_documents",
	MESSAGES_WITH_ACTIONS = "messages_with_actions",
}

export enum SupabaseTableEnum {
	ACCOUNTING_RECIPIENTS = "accounting_recipients",
	ACCOUNTING_VIEW = "accounting_view",
	ACTIVATION_CODES = "activation_codes",
	ALLOWED_SIGNUPS_LAB = "allowed_signups_lab",
	ARTICLES = "articles",
	ARTICLES_DEFAULT = "articles_default",
	BANK_ACCOUNTS = "bank_accounts",
	BRANDS = "brands",
	CLIENTS = "clients",
	CODE_PAIRS_RLS = "code_pairs_rls",
	DESKTOPS = "desktops",
	DESKTOP_JOB_REQUESTS = "desktop_job_requests",
	DESKTOP_JOB_RESPONSES = "desktop_job_responses",
	DESKTOP_SYSTEMS = "desktop_systems",
	EMPTY_PROFILES = "empty_profiles",
	EMAILS = "emails",
	FILES = "files",
	FORMS = "forms",
	GUARANTORS = "guarantors",
	INVOICES = "invoices",
	JOB_DOCUMENTS = "job_documents",
	JOB_DOCUMENTS_INVOICES_RELATIONSHIPS = "job_documents_invoices_relationships",
	JOB_ITEMS = "job_items",
	JOB_ITEMS_WITH_PATIENTS = "job_items_with_patients",
	JOB_REQUESTS = "job_requests",
	JOBS = "jobs",
	MESSAGES = "messages",
	SHARED_JOBS = "shared_jobs",
	TEMPLATES = "templates",
	TEMPLATE_ITEMS = "template_items",
	ORGANIZATIONS = "organizations",
	PATIENTS = "patients",
	PRACTITIONERS = "practitioners",
	PROFILES = "profiles",
	PROFILES_TO_INVITES_VIEW = "profiles_to_invites",
	REPLIES = "replies",
	SHARED = "shared",
	TARIFFS = "tariffs",
	TARIFFS_DEFAULT = "tariffs_default",
	TASKS = "tasks",
	MESSAGE_ACTIONS = "message_actions",
	CHANNELS = "channels",
}


export type SharesWithFileInfo =
	Database["public"]["Views"][SupabaseShareEntityViewEnum.SHARES_WITH_FILE_INFO]["Row"];
export type TaskEntityType =
	Database["public"]["Tables"][SupabaseTableEnum.TASKS]["Row"];

export type FileWithShare =
	Database["public"]["Views"][SupabaseShareEntityViewEnum.FILES_WITH_SHARES]["Row"];

export type JobWithShare =
	Database["public"]["Views"][SupabaseShareEntityViewEnum.JOBS_WITH_SHARES]["Row"];

export type ProfilesToInvitesViewType =
	Database["public"]["Views"][SupabaseTableEnum.PROFILES_TO_INVITES_VIEW]["Row"];

export enum SupabaseRpcEnum {
	CREATE_INVOICE_SINGLE = "create_invoice_single",
	CREATE_INVOICE_MONTHLY = "create_invoice_monthly",
	PROCESS_CAMT54_TRANSACTIONS = "process_camt54_transactions",
	SET_INVOICE_PAID = "set_invoice_paid",
	GET_JOB_ITEMS_CLIENT_STATS = "get_job_items_client_stats",
	GET_JOB_ITEMS_TECHN_STATS = "get_job_items_techn_stats",
	GET_JOB_ITEMS_CODE_STATS = "get_job_items_code_stats",
	SHARE_FILE = "share_file",
	UNSHARE_FILE = "unshare_file",
	SHARE_SRG = "share_srg",
	UNSHARE_SRG = "unshare_srg",
	GET_JOBS_WITH_PENDING_DOCS = "get_jobs_with_pending_docs",
	UPLOAD_AND_SHARE_JOB_FILE = "upload_and_share_job_file",
	GET_PENDING_JOBS = "get_pending_jobs",
	GET_INVOICE_BACKUP = "get_invoice_backup",
	GET_DELIVERY_NOTE_BACKUP = "get_delivery_note_backup",
	CONNECT_CLIENT = "connect_client",
	PLACEHOLDER_JOBS_TO_JOBS = "placeholder_jobs_to_jobs",
	ACCEPT_JOB_REQUEST = "accept_job_request",
	DENY_JOB_REQUEST = "deny_job_request",
	GET_JOB_DEFAULT_VALUES = "get_job_default_values",
}

export type RecycleBinFilesViewType =
	Database["public"]["Views"][SupabaseViewEnum.RECYCLE_BIN_FILES]["Row"];

export type RecycleBinJobDocumentsViewType =
	Database["public"]["Views"][SupabaseViewEnum.RECYCLE_BIN_JOB_DOCUMENTS]["Row"];

export type SharedJobsViewType =
	Database["public"]["Views"][SupabaseTableEnum.SHARED_JOBS]["Row"];

export type AccountingViewType =
	Database["public"]["Views"]["accounting_view"]["Row"];

/**
 * Entity in supabase "replies" table
 */
export type ReplyEntityType =
	Database["public"]["Tables"][SupabaseTableEnum.REPLIES]["Row"];

/**
 * Entity in supabase "activities" table
 */
export type ActivityEntityType =
	Database["public"]["Tables"][SupabaseTableEnum.SHARED]["Row"];

/**
 * Entity in supabase "activities" table for insert
 */
export type ActivityEntityTypeInsert =
	Database["public"]["Tables"][SupabaseTableEnum.SHARED]["Insert"];

/**
 * Entity in supabase "articles" table
 */
export type JobEntityType =
	| Database["public"]["Tables"][SupabaseTableEnum.JOBS]["Row"]
	| JobWithShare;

export type InsertJobEntityType =
	Database["public"]["Tables"][SupabaseTableEnum.JOBS]["Insert"];

/**
 * Entity in supabase "articles" table
 */
export type ArticleEntityType =
	Database["public"]["Tables"][SupabaseTableEnum.ARTICLES]["Row"];

/**
 * Entity in supabase "articles" table for insert
 */
export type ArticleEntityTypeInsert =
	Database["public"]["Tables"][SupabaseTableEnum.ARTICLES]["Insert"];

/**
 * Entity in supabase "articles_default" table
 */
export type ArticleDefaultEntityType =
	Database["public"]["Tables"][SupabaseTableEnum.ARTICLES_DEFAULT]["Row"];

/**
 * Entity in supabase "bank_accounts" table
 */
export type BankAccountEntityType =
	Database["public"]["Tables"][SupabaseTableEnum.BANK_ACCOUNTS]["Row"];

/**
 * Entity in supabase "clients" table
 */
export type ClientEntityType = Omit<
	Database["public"]["Tables"][SupabaseTableEnum.CLIENTS]["Row"],
	"search_vector"
>;

/**
 * Entity in supabase "guarantors" table
 */
export type GuarantorEntityType =
	Database["public"]["Tables"][SupabaseTableEnum.GUARANTORS]["Row"];

/**
 * Entity in supabase "job_documents" table
 */
export type JobDocumentEntityType =
	Database["public"]["Tables"][SupabaseTableEnum.JOB_DOCUMENTS]["Row"];

/**
 * Entity in supabase "job_items" table
 */
export type JobItemEntityType =
	Database["public"]["Tables"][SupabaseTableEnum.JOB_ITEMS]["Row"];

/**
 * Entity in supabase "job_items" table for insert
 */
export type JobItemEntityTypeInsert =
	Database["public"]["Tables"][SupabaseTableEnum.JOB_ITEMS]["Insert"];

/**
 * Entity in supabase "organizations" table
 */
export type OrganizationEntityType =
	Database["public"]["Tables"][SupabaseTableEnum.ORGANIZATIONS]["Row"];

/**
 * Entity in supabase "patients" table
 */
export type PatientEntityType = Omit<
	Database["public"]["Tables"][SupabaseTableEnum.PATIENTS]["Row"],
	"search_vector"
>;

/**
 * Entity in supabase "patients" table for update
 */
export type PatientEntityTypeUpdate =
	Database["public"]["Tables"][SupabaseTableEnum.PATIENTS]["Update"];

/**
 * Entity in supabase "practitioners" table
 */
export type PractitionerEntityType =
	Database["public"]["Tables"][SupabaseTableEnum.PRACTITIONERS]["Row"];

/**
 * Entity in supabase "profiles" table
 */
export type ProfileEntityType =
	Database["public"]["Tables"][SupabaseTableEnum.PROFILES]["Row"];

/**
 * Entity in supabase "tariffs" table
 */
export type TariffEntityType =
	Database["public"]["Tables"][SupabaseTableEnum.TARIFFS]["Row"];

/**
 * Entity in supabase "tariffs" table for insert
 */
export type TariffEntityTypeInsert =
	Database["public"]["Tables"][SupabaseTableEnum.TARIFFS]["Insert"];

/**
 * Entity in supabase "tariffs_default" table
 */
export type TariffDefaultEntityType =
	Database["public"]["Tables"][SupabaseTableEnum.TARIFFS_DEFAULT]["Row"];

/**
 * Entity in supabase "files" table
 */
export type FileEntityType =
	Database["public"]["Tables"][SupabaseTableEnum.FILES]["Row"];

/**
 * Entity in supabase "invoices" table
 */
export type InvoiceEntityType =
	Database["public"]["Tables"][SupabaseTableEnum.INVOICES]["Row"];

/**
 * Entity in supabase "desktops" table
 */
export type DesktopEntityType =
	Database["public"]["Tables"][SupabaseTableEnum.DESKTOPS]["Row"];

/**
 * Entity in supabase "desktop_systems" table
 */
export type DesktopSystemEntityType =
	Database["public"]["Tables"][SupabaseTableEnum.DESKTOP_SYSTEMS]["Row"];

/**
 * Entity in supabase "desktop_systems" table for insert
 */
export type DesktopSystemEntityTypeInsert =
	Database["public"]["Tables"][SupabaseTableEnum.DESKTOP_SYSTEMS]["Insert"];

/**
 * Entity in supabase "desktop_job_requests" table
 */
export type DesktopJobRequestEntityType =
	Database["public"]["Tables"][SupabaseTableEnum.DESKTOP_JOB_REQUESTS]["Row"];

/**
 * Entity in supabase "desktop_job_responses" table
 */
export type DesktopJobResponseEntityType =
	Database["public"]["Tables"][SupabaseTableEnum.DESKTOP_JOB_RESPONSES]["Row"];

/**
 * Entity of Desktop and its systems
 */
export type DesktopEntityTypeWithSystems = DesktopEntityType & {
	desktop_systems: DesktopSystemEntityType[];
};

export interface JobItemStatsRow {
	date: string;
	client_id?: string;
	technician_id?: string;
	status: number;
	total_jobs: number;
	total_job_items: number;
	amount_tariff: number;
	amount_article_material: number;
	amount_article_postage: number;
	amount_article_external_work: number;
	amount_article_discounts: number;
	amount_article_fixed_rate: number;
	amount_warranty: number;
	organization_id: string;
	total_count: number;
}

export interface GetJobItemsStatsParams {
	start_date: string;
	end_date: string;
	page_size?: number;
	page_number?: number;
}
